/* eslint-disable react/jsx-no-bind */
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import isEqualWith from 'lodash/isEqualWith';
import { styled } from '@compiled/react';
import { Prompt } from 'react-router-dom';

import { token } from '@atlaskit/tokens';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { N30 } from '@atlaskit/theme/colors';

import { ThirdPartyNudge } from '@confluence/third-party-nudge';
import { ADD_PAGE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker';
import { ProfileAvatar } from '@confluence/profile';
import { PageCommentEditor } from '@confluence/comment-simple';
import { Attribution, GenericErrorBoundary } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { withSessionData } from '@confluence/session-data';

import { scrollElementToViewport } from '../../helpers/dom';
import { EDITOR_VERSIONS } from '../../constants/editor-constants';
import { NEW_COMMENT_ID, ERROR_TYPES } from '../CommentsSection/helpers/constants';

import CommentAddPrompt from './CommentAddPrompt';
import CommentSpinner from './CommentSpinner';
import PageCommentRenderer from './PageCommentRenderer';
import { CommentSaveWarning } from './CommentSaveWarning';

const COMMENT_DEPTH_STEP = 40;

const noop = () => {};

const compareIgnoreEditor = (current, next, key, props) => {
	if (key === 'editor') {
		return props.mode === 'view' || current.isEditorLoaded === next.isEditorLoaded;
	}
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageCommentWrapper = styled.div({
	position: 'relative',
	padding: 0,
	marginBottom: token('space.300', '24px'),
	overflowY: 'visible',
	minHeight: '35px',
	overflowWrap: 'break-word',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: `${(props) => COMMENT_DEPTH_STEP * props.comment.depthLevel}px`,
	paddingTop: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarContainer = styled.div({
	width: '32px',
	height: '32px',
	marginRight: '10px',
	float: 'left',
});

/**z-index is set to 8 so sticky Editor is under the Inline comment box(z-index: 9)
 **/
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddCommentWrapper = styled.div({
	position: 'sticky',
	bottom: 0,
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	background: `${token('elevation.surface', 'white')} !important`,
	paddingBottom: token('space.200', '16px'),
	borderRadius: '3px',
	zIndex: 4,
	'&::before': {
		content: ' ',
		display: 'block',
		marginBottom: token('space.200', '16px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		borderBottom: (props) =>
			props.totalComments > 0 ? `1px solid ${token('color.border', N30)}` : 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentContainer = styled.div({
	position: 'relative',
	marginLeft: '51px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ThirdPartyNudgeContainer = styled.div({
	display: 'flex',
	'justify-content': 'end',
	height: 0,
	'z-index': '10',
	position: 'relative',
	top: '-9px',
});

export const Comment = withSessionData(
	withAnalyticsEvents()(
		class Comment extends Component {
			static propTypes = {
				/**
				 * Comment data
				 */
				comment: PropTypes.shape({
					/**
					 * List of ancestor id of comment
					 */
					ancestors: PropTypes.array,
					/**
					 * ADF for rendering comment
					 */
					adf: PropTypes.object,
					/**
					 * Comment ID
					 */
					id: PropTypes.string,
					/**
					 * comment details
					 */
					version: PropTypes.shape({
						number: PropTypes.number,
						id: PropTypes.string,
						when: PropTypes.string,
						by: PropTypes.object,
					}),
					/**
					 * Author details
					 */
					createdBy: PropTypes.shape({
						isAnonymous: PropTypes.bool,
						displayName: PropTypes.string,
						avatarUrl: PropTypes.string,
						accountId: PropTypes.string,
						permissionType: PropTypes.string,
					}),
					/**
					 * Is Comment created by Page Author
					 */
					createdByAuthor: PropTypes.bool,
					/**
					 * Comment link
					 */
					linkWebui: PropTypes.string,
					/**
					 * Permitted operations
					 */
					operations: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
					/**
					 * Comment link
					 */
					createdFriendly: PropTypes.string,
					/**
					 * The status of the comment
					 */
					contentStatus: PropTypes.string,
					/**
					 * Permissions for comments API
					 */
					permissions: PropTypes.shape({
						isEditable: PropTypes.bool,
						isRemovable: PropTypes.bool,
						isViewable: PropTypes.bool,
					}),
					/**
					 * Was comment created on mobile
					 */
					isFromMobile: PropTypes.bool,
				}),
				/**
				 * Defines can current user view other users profiles and hovers data
				 */
				canViewProfile: PropTypes.bool,
				/**
				 * The content id that the comment belongs to
				 */
				contentId: PropTypes.string.isRequired,
				/**
				 * Content Type of the page
				 */
				contentType: PropTypes.oneOf(['blogpost', 'page']),
				/**
				 * Defines should we highlight this comment as focused
				 */
				isFocused: PropTypes.bool,

				isHighlighted: PropTypes.bool,

				/**
				 * Defines should we show author label on comment
				 */
				showAuthorLabel: PropTypes.bool,
				/**
				 * Comment mode
				 */
				mode: PropTypes.oneOf(['view', 'edit', 'add']),
				/**
				 * Comment author user info
				 */
				userInfo: PropTypes.object,
				/**
				 * Is editor enabled (just for add mode)
				 */
				isEditorEnabled: PropTypes.bool,
				/**
				 * Object with actions for comment
				 */
				commentActions: PropTypes.object,
				/**
				 * Editor properties
				 */
				editor: PropTypes.object,
				/**
				 * Custom editor save handler
				 */
				editorSaveHandler: PropTypes.func,
				/**
				 * Custom editor cancel handler
				 */
				editorCancelHandler: PropTypes.func,
				/**
				 * onClick event handler for comment add prompt to display editor
				 */
				onClickCommentAddPrompt: PropTypes.func,
				/**
				 * Callback function when editor has been loaded
				 */
				onEditorLoaded: PropTypes.func,
				/**
				 * Callback function when the editor has changes.
				 */
				onEditorContentChanged: PropTypes.func,
				/**
				 * A factory function injected by the withAnalyticsEvents annotation to generate analytics events
				 */
				createAnalyticsEvent: PropTypes.func.isRequired,
				/**
				 * SpaceId of current space
				 */
				spaceId: PropTypes.string.isRequired,
				/**
				 * Reply comment permission
				 */
				canReplyToComment: PropTypes.bool,
				/**
				 * Should comment be autofocused
				 */
				shouldAutofocus: PropTypes.bool,
				onAutoFocused: PropTypes.func,
				experienceTracker: PropTypes.object.isRequired,
				/**
				 * A feature flag to control aborting add-page-comment more aggressively to see how it affects SLA
				 */
				useNewExperienceAbort: PropTypes.bool,
				/**
				 * A feature flag to control triggering a 3P nudge
				 */
				isThirdPartyNudgeEnabled: PropTypes.bool,
				onRendered: PropTypes.func,
				totalComments: PropTypes.number,
				sessionData: PropTypes.object,
				currentUserAccountId: PropTypes.string.isRequired,
			};

			static defaultProps = {
				comment: {},
				canViewProfile: false,
				isFocused: false,
				isHighlighted: false,
				showAuthorLabel: false,
				userInfo: {},
				isEditorEnabled: false,
				commentActions: {},
				editor: {},
				editorSaveHandler: () => {},
				editorCancelHandler: () => {},
				onClickCommentAddPrompt: () => {},
				onEditorLoaded: () => {},
				onEditorContentChanged: () => {},
				canReplyToComment: false,
				useNewExperienceAbort: false,
				isThirdPartyNudgeEnabled: false,
				onRendered: () => {},
				totalComments: 0,
				sessionData: {},
			};

			constructor(...args) {
				super(...args);
				this._editorCancelHandler = this._editorCancelHandler.bind(this);
				this._editorAfterActivate = this._editorAfterActivate.bind(this);
				this.shouldFocusAddPromptRef = React.createRef();
			}

			state = {
				updatedAdf: null,
			};

			shouldComponentUpdate(nextProps) {
				// Every Comment component receives `editor` object as a prop. This prop reflects
				// the Editor lifecycle related to lazy-loading the Editor component, rendering it
				// in the edit mode and hiding after completing editing.
				// The logic below ensures that Comment components in a "view" mode are not rendered
				// when `editor` prop is updated.
				const comparer =
					this.props.mode === nextProps.mode &&
					this.props.editor.errorType === nextProps.editor.errorType
						? compareIgnoreEditor
						: noop; // compare everything

				return !isEqualWith(this.props, nextProps, comparer);
			}

			_editorSaveHandler = (props) => {
				const version = {};
				const {
					editorSaveHandler,
					contentId,
					comment: { id: commentId },
					editor: { mode },
				} = this.props;

				if (mode === 'editComment') {
					const {
						comment: {
							version: { number },
						},
					} = this.props;
					version.number = number + 1;
				}
				this.setState({ updatedAdf: null });

				return editorSaveHandler({ contentId, commentId, version, ...props });
			};

			_editorCancelHandler(editorProps) {
				if (editorProps.isEditorAborted) {
					this.shouldFocusAddPromptRef.current = true;
				}
				this.setState({ updatedAdf: null });

				return this.props.editorCancelHandler(editorProps);
			}

			_editorAfterActivate(commentContainer) {
				const { mode } = this.props;
				//scroll to bottom of page on focusing the Editor.
				if (mode === 'add') {
					window.scrollTo(0, document.body.scrollHeight);
				}
				//getting Editor DOM Reference from CommentEditorComponent to scroll to Editor
				scrollElementToViewport(commentContainer, {
					topGap: 60,
					bottomGap: 10,
					duration: 100,
				});
			}

			_renderCommentEditor() {
				const {
					comment,
					onEditorLoaded,
					onEditorContentChanged,
					contentId,
					contentType,
					editor,
					spaceId,
					mode,
					experienceTracker,
					useNewExperienceAbort,
				} = this.props;

				if (mode === 'edit' && editor.mode !== 'replyComment' && !comment.adf) {
					return null;
				}

				function handleExperienceAbortOnNavigation() {
					experienceTracker && experienceTracker.abort(ADD_PAGE_COMMENT_EXPERIENCE);
				}

				const commentMode =
					mode === 'edit' && editor.mode !== 'replyComment'
						? 'edit'
						: editor.mode === 'replyComment'
							? 'reply'
							: 'create';

				const commentEditor = (
					<PageCommentEditor
						contentId={contentId}
						contentType={contentType}
						mode={commentMode}
						spaceId={spaceId}
						saveHandler={this._editorSaveHandler}
						content={this.state.updatedAdf || comment.adf}
						cancelHandler={this._editorCancelHandler}
						onEditorLoaded={onEditorLoaded}
						onEditorContentChanged={onEditorContentChanged}
						afterActivate={this._editorAfterActivate}
					/>
				);

				return useNewExperienceAbort ? (
					<>
						{commentEditor}
						<Prompt message={handleExperienceAbortOnNavigation} />
					</>
				) : (
					commentEditor
				);
			}

			handleEditorError(error) {
				const { editor, useNewExperienceAbort, experienceTracker, sessionData } = this.props;

				if (!useNewExperienceAbort) {
					return;
				}

				/*
				 * WS-2956
				 * If we encounter an error while loading the editor, we need to immediately start and fail the experience to handle the loading errors.
				 * We have code elsewhere to track and handle mutation failures related to the publish flow so this will only affect the loading flows.
				 */
				experienceTracker.start({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					attributes: {
						editor: EDITOR_VERSIONS.FABRIC,
						mode: editor.mode,
						isSingleExperience: true,
						edition: sessionData?.edition,
					},
				});

				experienceTracker.fail({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					error,
				});

				markErrorAsHandled(error);
			}

			render() {
				const {
					comment,
					contentId,
					contentType,
					onClickCommentAddPrompt,
					editor,
					isEditorEnabled,
					mode,
					isFocused,
					isHighlighted,
					commentActions,
					canViewProfile,
					userInfo,
					showAuthorLabel,
					canReplyToComment,
					shouldAutofocus,
					onAutoFocused,
					onRendered,
					totalComments,
					spaceId,
					createAnalyticsEvent,
					isThirdPartyNudgeEnabled,
				} = this.props;

				const commentProps = {};

				if (mode === 'view' || mode === 'edit') {
					commentProps.id = `comment-${comment.id}`;
					commentProps['data-test-id'] = `comment-${comment && comment.id ? comment.id : 'new'}`;
				}

				if (mode === 'add') {
					commentProps.id = 'addcomment';
					commentProps.totalComments = totalComments;
				}

				const isReply = editor.mode === 'replyComment';

				if (isReply) {
					commentProps['data-parent-comment-id'] = comment.id;
				}

				const showAddCommentSpinner = mode === 'add' && isEditorEnabled && !editor.isEditorLoaded;

				const showEditCommentSpinner =
					mode === 'edit' &&
					(!editor.isEditorLoaded || (!comment.adf && editor.mode === 'editComment'));

				const showSpinner = showAddCommentSpinner || showEditCommentSpinner;

				const CommentWrapper = mode === 'add' ? AddCommentWrapper : PageCommentWrapper;

				const name = userInfo?.displayName ?? '';

				const handleError = (error) => this.handleEditorError(error);

				const isCommentAuthor = comment?.createdBy?.accountId === userInfo?.accountId;

				return mode === 'view' ? (
					<>
						{isThirdPartyNudgeEnabled && isHighlighted && isCommentAuthor && (
							<ThirdPartyNudgeContainer>
								<ThirdPartyNudge trigger="comment-section" showImage width={400} />
							</ThirdPartyNudgeContainer>
						)}
						<PageCommentRenderer
							contentId={contentId}
							contentType={contentType}
							spaceId={spaceId}
							comment={comment}
							commentActions={commentActions}
							canReplyToComment={canReplyToComment}
							canViewProfile={canViewProfile}
							isFocused={isFocused || isHighlighted}
							showAuthorLabel={showAuthorLabel}
							shouldAutofocus={shouldAutofocus}
							onAutoFocused={onAutoFocused}
							onRendered={onRendered}
							createAnalyticsEvent={createAnalyticsEvent}
							currentUserAccountId={this.props.currentUserAccountId}
						/>
					</>
				) : (
					<GenericErrorBoundary
						attribution={Attribution.COLLABORATION}
						/* eslint-disable-next-line react/jsx-no-bind */
						onError={handleError}
						renderOnError={noop}
					>
						<CommentWrapper {...commentProps} comment={comment}>
							<AvatarContainer>
								<ProfileAvatar
									canViewProfile={canViewProfile}
									src={userInfo.avatarUrl}
									userId={userInfo.accountId}
									isAnonymous={!userInfo.accountId}
									name={name}
									testId="profile-avatar-comment"
								/>
							</AvatarContainer>
							<CommentContainer>
								{mode === 'edit' ? this._renderCommentEditor() : null}
								{mode !== 'add' ? null : isEditorEnabled ? (
									this._renderCommentEditor()
								) : (
									<Fragment>
										<CommentAddPrompt
											onClick={onClickCommentAddPrompt}
											autofocusRef={this.shouldFocusAddPromptRef}
										/>
									</Fragment>
								)}
								{showSpinner ? <CommentSpinner onDidMount={this._editorAfterActivate} /> : null}
							</CommentContainer>
							{editor.errorType === ERROR_TYPES.KNOWN_ERROR &&
							(editor.commentId === comment.id || editor.commentId === NEW_COMMENT_ID) ? (
								<CommentSaveWarning errorMessage={editor.formattedErrorMessage} />
							) : null}
						</CommentWrapper>
					</GenericErrorBoundary>
				);
			}
		},
	),
);
